import axios from 'axios';
import { iframeDataBridge } from '../iframe/iframeDataBridge.js';
import { getApiUrl, getAuthHeaders } from '../utils/authService.js';

let travelMode = 'car';

function getEcosOptions() {
	const ecosVM = iframeDataBridge.getEcosVM(true);
	return ecosVM?.options || {};
}

function isGoogleAllowed() {
	const options = getEcosOptions();
	const preferGoogle = Boolean(options.find((o) => o.name == 'GeoProviderGoogle')?.value);
	const provider = preferGoogle ? 'google' : 'osrm';

	if (provider !== 'google') return false;

	const maxRemoteCall = Number(options.find((o) => o.name == 'MaxRemoteCall.google.maps.DirectionsService')?.value) || 0;
	const remoteCallCount = Number(options.find((o) => o.name == 'MaxRemoteCall.google.maps.DirectionsService')?.currentValue) || 0;
	if (maxRemoteCall <= 0) return true;
	return provider === 'google' && remoteCallCount < maxRemoteCall;
}

function incGoogleCall() {
	const options = getEcosOptions();
	const option = options.find((o) => o.name == 'MaxRemoteCall.google.maps.DirectionsService');
	if (option) {
		option.currentValue = ((Number(option.currentValue) || 0) + 1).toString();
	}

	const url = getApiUrl(`/Core/Options`);
	const headersPromise = getAuthHeaders({}, 'token');
	headersPromise.then((headers) => {
		axios.post(url, [option], { headers: headers });
	});
}

function formatDurationSeconds(durationSeconds) {
	if (typeof durationSeconds !== 'number' || Number.isNaN(durationSeconds)) return '';
	const totalMinutes = Math.round(durationSeconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	const hh = String(hours).padStart(2, '0');
	const mm = String(minutes).padStart(2, '0');
	return `${hh}h${mm}`;
}

function formatDistanceMeters(distanceMeters) {
	if (typeof distanceMeters !== 'number' || Number.isNaN(distanceMeters)) return '';
	const km = distanceMeters / 1000;
	const rounded = km >= 10 ? Math.round(km) : Math.round(km * 10) / 10;
	return `${rounded} km`;
}

function buildGeoJsonLine(coordinates, distanceText, durationText, distanceMeters, durationSeconds) {
	return {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				geometry: {
					type: 'LineString',
					coordinates: coordinates,
				},
				properties: {
					distance: distanceText,
					duration: durationText,
					distance_value: distanceMeters,
					duration_value: durationSeconds,
				},
			},
		],
	};
}

async function getRouteWithOSRM(waypoints) {
	if (!Array.isArray(waypoints) || waypoints.length < 2) {
		throw new Error('OSRM requires at least 2 waypoints');
	}

	const coords = waypoints.map((p) => `${p[0]},${p[1]}`).join(';');
	const url = getApiUrl(`/geo/osrm/route/v1/driving/${coords}`);
	const params = {
		overview: 'full',
		geometries: 'geojson',
		steps: 'false',
	};
	const headers = await getAuthHeaders({}, 'token');
	const response = await axios.get(url, { params: params, headers: headers });
	const data = response?.data || null;
	const route = data?.routes?.[0] || null;
	const legs = route?.legs || null;

	if (!route || !route.geometry || !route.geometry.coordinates || !legs || !legs.length) {
		throw new Error('OSRM route format invalid');
	}

	let distanceMeters = 0;
	let durationSeconds = 0;

	legs.forEach((leg) => {
		distanceMeters += Number(leg.distance || 0);
		durationSeconds += Number(leg.duration || 0);
	});

	const distanceText = formatDistanceMeters(distanceMeters);
	const durationText = formatDurationSeconds(durationSeconds);

	return buildGeoJsonLine(route.geometry.coordinates, distanceText, durationText, distanceMeters, durationSeconds);
}

async function getRouteWithGoogleMaps(waypoints) {
	if (!Array.isArray(waypoints) || waypoints.length < 2) {
		throw new Error('Google Maps requires at least 2 waypoints');
	}

	const iframeWin = iframeDataBridge.getIframeWindow();
	const google = iframeWin?.google;

	if (!google || !google.maps) {
		throw new Error('Google Maps API not available');
	}

	if (!isGoogleAllowed()) {
		throw new Error('Google Maps call limit reached');
	}

	incGoogleCall();

	const originPoint = waypoints[0];
	const destinationPoint = waypoints[waypoints.length - 1];
	const intermediateWaypoints = waypoints.slice(1, waypoints.length - 1);

	const travelModeMap = { walking: 'WALK', bike: 'BICYCLE', car: 'DRIVE' };
	const makeWaypoint = (p) => ({ location: { latLng: { latitude: p[1], longitude: p[0] } } });

	const request = {
		origin: makeWaypoint(originPoint),
		destination: makeWaypoint(destinationPoint),
		intermediates: intermediateWaypoints.map(makeWaypoint),
		travelMode: travelModeMap[travelMode] || 'DRIVE',
		computeAlternativeRoutes: false,
		polylineQuality: 'HIGH_QUALITY',
		polylineEncoding: 'ENCODED_POLYLINE',
	};

	let RouteClass = google.maps.routes?.Route;
	if (!RouteClass && typeof google.maps.importLibrary === 'function') {
		const routesLib = await google.maps.importLibrary('routes');
		RouteClass = routesLib.Route || routesLib.RoutesClient;
	}
	if (!RouteClass) {
		throw new Error('google.maps.routes.Route not available');
	}

	const routesClient = new RouteClass();
	const response = await routesClient.computeRoutes(request, {
		otherArgs: {
			headers: {
				'X-Goog-FieldMask': 'routes.legs.distanceMeters,routes.legs.duration,routes.polyline.encodedPolyline',
			},
		},
	});

	const route = response?.routes?.[0] || null;
	const legs = route?.legs || null;
	const encodedPolyline = route?.polyline?.encodedPolyline || null;

	if (!route || !legs || !legs.length || !encodedPolyline) {
		throw new Error('Google route format invalid');
	}

	let distanceMeters = 0;
	let durationSeconds = 0;

	legs.forEach((leg) => {
		distanceMeters += Number(leg?.distanceMeters || 0);
		durationSeconds += Math.round(parseFloat(leg?.duration) || 0);
	});

	const path = google.maps.geometry.encoding.decodePath(encodedPolyline);
	const coordinates = path.map((p) => [p.lng(), p.lat()]);
	const distanceText = formatDistanceMeters(distanceMeters);
	const durationText = formatDurationSeconds(durationSeconds);

	return buildGeoJsonLine(coordinates, distanceText, durationText, distanceMeters, durationSeconds);
}

async function getRoute(waypoints, forcedProvider = null) {
	if (isGoogleAllowed()) {
		return await getRouteWithGoogleMaps(waypoints);
	}
	return await getRouteWithOSRM(waypoints);
}

function setTravelMode(mode) {
	if (['car', 'walking', 'bike'].includes(mode)) {
		travelMode = mode;
	}
}
 
function getTravelMode() {
	return travelMode;
}

async function getRouteWithFallback(waypoints) {
	if (isGoogleAllowed()) {
		try {
			return await getRouteWithGoogleMaps(waypoints);
		} catch (e) {
			return await getRouteWithOSRM(waypoints);
		}
	}

	return await getRouteWithOSRM(waypoints);
}

export const routeService = {
	getRoute: getRoute,
	getRouteWithFallback: getRouteWithFallback,
	setTravelMode: setTravelMode,
	getTravelMode: getTravelMode,
	isGoogleAllowed: isGoogleAllowed,
};
